import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  LinearProgress,
  Fade,
  Autocomplete,
  TextField,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Image as ImageIcon,
  Code as CodeIcon,
} from "@mui/icons-material";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { BASE_URL } from "../../../services/configUrls";
import { AppleStyleSpinner } from "./MemCreation/AppleStyleSpinner";

const CreateMOUModal = ({
  open,
  onClose,
  instituteId,
  membershipId,
  onSuccess,
}) => {
  // Membership selection states
  const [memberships, setMemberships] = useState([]);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [loadingMemberships, setLoadingMemberships] = useState(false);
  const [membershipError, setMembershipError] = useState("");

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [exhibitContent, setExhibitContent] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const quillRef = useRef(null);

  // Tooltip states
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [selectedRange, setSelectedRange] = useState(null);
  const selectionTimeoutRef = useRef(null);

  // Validation constants
  const MAX_FILE_SIZE = 5 * 1024 * 1024;
  const ALLOWED_FILE_TYPES = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/svg+xml",
  ];
  const ALLOWED_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".svg"];

  const MIN_WIDTH = 200;
  const MIN_HEIGHT = 50; // Changed from 200 to 50 for horizontal logos
  const MAX_WIDTH = 4000; // Increased as per your previous request
  const MAX_HEIGHT = 2000;
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [] }],
      ["link"],
      ["clean"],
    ],
    clipboard: {
      matchVisual: false,
    },
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "indent",
    "align",
    "link",
  ];

  // Fetch memberships when modal opens
  useEffect(() => {
    if (open && instituteId) {
      fetchMemberships();
    }
  }, [open, instituteId]);

  const fetchMemberships = async () => {
    setLoadingMemberships(true);
    setMembershipError("");
    try {
      const response = await axios.get(
        `${BASE_URL}/accounts/institute/${instituteId}/membership-info`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      setMemberships(response.data.memberships || []);
    } catch (error) {
      console.error("Error fetching memberships:", error);
      setMembershipError(
        error.response?.data?.message || "Failed to load memberships"
      );
      setMemberships([]);
    } finally {
      setLoadingMemberships(false);
    }
  };

  const handleSelectionChange = (range, source, editor) => {
    if (selectionTimeoutRef.current) {
      clearTimeout(selectionTimeoutRef.current);
    }

    setShowTooltip(false);

    if (range && range.length > 0 && source === "user") {
      const selectedText = editor.getText(range.index, range.length);

      if (
        selectedText &&
        selectedText.trim() &&
        selectedText.trim().length > 0
      ) {
        selectionTimeoutRef.current = setTimeout(() => {
          const bounds = quillRef.current.getEditor().getBounds(range.index);
          const editorContainer = quillRef.current.getEditor().container;
          const containerRect = editorContainer.getBoundingClientRect();

          setTooltipPosition({
            top: containerRect.top + bounds.top - 40,
            left: containerRect.left + bounds.left + bounds.width / 2,
          });
          setSelectedRange(range);
          setShowTooltip(true);
        }, 300);
      }
    }
  };

  const insertInstituteName = () => {
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection(true);
      editor.insertText(range.index, "{{ institute_name }}", "user");
      editor.setSelection(range.index + 18);
    }
    setShowTooltip(false);
  };

  const insertInstituteNameAtSelection = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const editor = quillRef.current?.getEditor();
    if (editor && selectedRange) {
      editor.deleteText(selectedRange.index, selectedRange.length);
      editor.insertText(selectedRange.index, "{{ institute_name }}", "user");
      editor.setSelection(selectedRange.index + 18);
    }

    setShowTooltip(false);
    setSelectedRange(null);
  };

  const validateFile = (file) => {
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return "Please upload a valid image file (JPG, JPEG, PNG, GIF, or SVG)";
    }
    if (file.size > MAX_FILE_SIZE) {
      return "File size must be less than 5MB";
    }
    return null;
  };

  const validateImageDimensions = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new Image();

        img.onload = () => {
          const width = img.width;
          const height = img.height;

          if (file.type === "image/svg+xml" && (width === 0 || height === 0)) {
            resolve({ width: null, height: null, dataUrl: e.target.result });
            return;
          }

          if (width < MIN_WIDTH || height < MIN_HEIGHT) {
            reject(
              `Image dimensions must be at least ${MIN_WIDTH}x${MIN_HEIGHT}px. Current: ${width}x${height}px`
            );
          } else if (width > MAX_WIDTH || height > MAX_HEIGHT) {
            reject(
              `Image dimensions must not exceed ${MAX_WIDTH}x${MAX_HEIGHT}px. Current: ${width}x${height}px`
            );
          } else {
            resolve({ width, height, dataUrl: e.target.result });
          }
        };

        img.onerror = () => reject("Failed to load image");
        img.src = e.target.result;
      };

      reader.onerror = () => reject("Failed to read file");
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    setError("");
    setSelectedFile(null);
    setPreviewUrl(null);

    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      event.target.value = "";
      return;
    }

    try {
      const { width, height, dataUrl } = await validateImageDimensions(file);
      setSelectedFile(file);
      setPreviewUrl(dataUrl);

      if (width && height) {
        console.log(`Image dimensions: ${width}x${height}px`);
      }
    } catch (dimensionError) {
      setError(dimensionError);
      event.target.value = "";
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setError("");
  };

  const handleEditorChange = (content, delta, source) => {
    setExhibitContent(content);
    if (source === "user") {
      setShowTooltip(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedMembership) {
      setError("Please select a membership");
      return;
    }

    if (!selectedFile) {
      setError("Please select a logo file");
      return;
    }

    const cleanContent = exhibitContent.replace(/<p><br><\/p>/g, "").trim();

    if (!cleanContent || cleanContent === "<p><br></p>") {
      setError("Please add Exhibit A content");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("institute_id", instituteId);
      formData.append(
        "institute_membership_id",
        selectedMembership.institute_membership_id
      );
      formData.append("logo_file", selectedFile);
      formData.append("exhibit_a_content", cleanContent);

      const response = await axios.post(
        `${BASE_URL}/accounts/create`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      alert(response?.data?.detail || "MOU created successfully");
      handleClose();
      onSuccess?.();
    } catch (error) {
      console.error("Error creating MOU:", error);
      alert(
        error.response?.data?.detail ||
          "Failed to create MOU. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedFile(null);
      setPreviewUrl(null);
      setExhibitContent("");
      setError("");
      setShowTooltip(false);
      setSelectedRange(null);
      setSelectedMembership(null);
      setMemberships([]);
      setMembershipError("");
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }
      onClose();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            maxWidth: "800px",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #E5E7EB",
            pb: 2,
            px: 3,
            pt: 2.5,
          }}
        >
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 600,
              color: "#1F1F1F",
              letterSpacing: "-0.01em",
            }}
          >
            Create MOU
          </Typography>
          <IconButton
            onClick={handleClose}
            disabled={loading}
            size="small"
            sx={{
              color: "#9CA3AF",
              "&:hover": {
                backgroundColor: "#F3F4F6",
                color: "#6B7280",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 4 }}>
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3, fontSize: "13px" }}
              onClose={() => setError("")}
            >
              {error}
            </Alert>
          )}

          {membershipError && (
            <Alert severity="error" sx={{ mb: 3, fontSize: "13px" }}>
              {membershipError}
            </Alert>
          )}

          {/* Membership Selection Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              sx={{
                fontSize: "15px",
                fontWeight: 600,
                color: "#1F1F1F",
                mb: 2,
              }}
            >
              Select Membership
            </Typography>

            <Autocomplete
              value={selectedMembership}
              onChange={(event, newValue) => {
                setSelectedMembership(newValue);
                setError("");
              }}
              options={memberships}
              getOptionLabel={(option) =>
                `${option.institute_membership_name} (${option.membership_status})`
              }
              loading={loadingMemberships}
              disabled={loading || loadingMemberships}
              isOptionEqualToValue={(option, value) =>
                option.institute_membership_id === value.institute_membership_id
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Choose a membership"
                  variant="outlined"
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: "13px",
                      backgroundColor: "#FFFFFF",
                      "& fieldset": {
                        borderColor: "#D1D5DB",
                      },
                      "&:hover fieldset": {
                        borderColor: "#9CA3AF",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#3B82F6",
                        borderWidth: "1px",
                      },
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingMemberships ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    fontSize: "13px",
                    py: 1.5,
                    px: 2,
                    "&:hover": {
                      backgroundColor: "#F9FAFB",
                    },
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "#1F1F1F",
                      }}
                    >
                      {option.institute_membership_name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        color: "#6B7280",
                        mt: 0.25,
                        textTransform: "capitalize",
                      }}
                    >
                      Status: {option.membership_status.replace(/_/g, " ")}
                    </Typography>
                  </Box>
                </Box>
              )}
              noOptionsText={
                loadingMemberships
                  ? "Loading memberships..."
                  : "No memberships available"
              }
              sx={{
                "& .MuiAutocomplete-listbox": {
                  maxHeight: "250px",
                },
              }}
            />

            <Typography
              sx={{
                fontSize: "12px",
                color: "#6B7280",
                mt: 1.5,
              }}
            >
              Select the membership for which you want to create the MOU
            </Typography>
          </Box>

          {/* File Upload Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              sx={{
                fontSize: "15px",
                fontWeight: 600,
                color: "#1F1F1F",
                mb: 2,
              }}
            >
              Upload Logo
            </Typography>

            {!selectedFile ? (
              <>
                <Button
                  variant="outlined"
                  component="label"
                  disabled={loading}
                  sx={{
                    textTransform: "none",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#374151",
                    borderColor: "#D1D5DB",
                    "&:hover": {
                      borderColor: "#9CA3AF",
                      backgroundColor: "#F9FAFB",
                    },
                    px: 3,
                    py: 1.5,
                  }}
                >
                  Choose File
                  <input
                    type="file"
                    hidden
                    accept={ALLOWED_EXTENSIONS.join(",")}
                    onChange={handleFileChange}
                    disabled={loading}
                  />
                </Button>
                <Typography
                  sx={{
                    fontSize: "13px",
                    color: "#6B7280",
                    mt: 1.5,
                  }}
                >
                  JPG, JPEG, PNG, GIF or SVG (max. 5MB)
                </Typography>
              </>
            ) : (
              <Box
                sx={{
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                  p: 2.5,
                  backgroundColor: "#FFFFFF",
                }}
              >
                <Box
                  sx={{ display: "flex", gap: 2.5, alignItems: "flex-start" }}
                >
                  <Box
                    sx={{
                      width: "100px",
                      height: "100px",
                      borderRadius: "8px",
                      overflow: "hidden",
                      border: "1px solid #E5E7EB",
                      flexShrink: 0,
                      backgroundColor: "#F9FAFB",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {previewUrl ? (
                      <img
                        src={previewUrl}
                        alt="Preview"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          padding: "8px",
                        }}
                      />
                    ) : (
                      <ImageIcon sx={{ fontSize: 40, color: "#9CA3AF" }} />
                    )}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#1F1F1F",
                        mb: 0.5,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {selectedFile.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: "#6B7280",
                        mb: 2,
                      }}
                    >
                      {formatFileSize(selectedFile.size)}
                    </Typography>
                    <Button
                      size="small"
                      onClick={handleRemoveFile}
                      disabled={loading}
                      sx={{
                        textTransform: "none",
                        fontSize: "13px",
                        color: "#EF4444",
                        "&:hover": {
                          backgroundColor: "#FEE2E2",
                        },
                        px: 1.5,
                        py: 0.5,
                        minWidth: "auto",
                        fontWeight: 500,
                      }}
                    >
                      Remove
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}

            <Box sx={{ mt: 2.5 }}>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#6B7280",
                  mb: 0.5,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 0.5,
                }}
              >
                <span style={{ marginTop: "2px" }}>•</span>
                <span>File must be less than 5MB</span>
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#6B7280",
                  mb: 0.5,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 0.5,
                }}
              >
                <span style={{ marginTop: "2px" }}>•</span>
                <span>Supported formats: JPG, JPEG, PNG, GIF, SVG</span>
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#6B7280",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 0.5,
                }}
              >
                <span style={{ marginTop: "2px" }}>•</span>
                <span>
                  Dimensions: {MIN_WIDTH}x{MIN_HEIGHT}px to {MAX_WIDTH}x
                  {MAX_HEIGHT}px (for raster images)
                </span>
              </Typography>
            </Box>
          </Box>

          {/* Rich Text Editor Section */}
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: "15px",
                  fontWeight: 600,
                  color: "#1F1F1F",
                }}
              >
                Exhibit A Content
              </Typography>
              <Button
                size="small"
                startIcon={<CodeIcon sx={{ fontSize: 16 }} />}
                onClick={insertInstituteName}
                disabled={loading}
                sx={{
                  textTransform: "none",
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#3B82F6",
                  "&:hover": {
                    backgroundColor: "#EFF6FF",
                  },
                  px: 1.5,
                  py: 0.5,
                }}
              >
                Insert Institute Name
              </Button>
            </Box>

            <Box
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                overflow: "hidden",
                backgroundColor: "#FFFFFF",
                position: "relative",
                "& .ql-container": {
                  fontSize: "14px",
                  fontFamily: "'Inter', -apple-system, sans-serif",
                  minHeight: "250px",
                  maxHeight: "350px",
                  overflowY: "auto",
                },
                "& .ql-toolbar": {
                  backgroundColor: "#F9FAFB",
                  borderBottom: "1px solid #E5E7EB",
                  borderTop: "none",
                  borderLeft: "none",
                  borderRight: "none",
                },
                "& .ql-editor": {
                  minHeight: "250px",
                  maxHeight: "350px",
                  padding: "16px",
                },
                "& .ql-editor.ql-blank::before": {
                  color: "#9CA3AF",
                  fontStyle: "normal",
                  fontSize: "14px",
                },
                "& .ql-stroke": {
                  stroke: "#6B7280",
                },
                "& .ql-fill": {
                  fill: "#6B7280",
                },
                "& .ql-picker-label": {
                  color: "#6B7280",
                },
                "& .ql-toolbar button:hover .ql-stroke": {
                  stroke: "#3B82F6",
                },
                "& .ql-toolbar button:hover .ql-fill": {
                  fill: "#3B82F6",
                },
                "& .ql-toolbar button.ql-active .ql-stroke": {
                  stroke: "#3B82F6",
                },
                "& .ql-toolbar button.ql-active .ql-fill": {
                  fill: "#3B82F6",
                },
              }}
            >
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={exhibitContent}
                onChange={handleEditorChange}
                onChangeSelection={handleSelectionChange}
                modules={modules}
                formats={formats}
                placeholder="Enter custom content for Exhibit A or paste HTML directly. Select text to insert template variable."
                readOnly={loading}
              />
            </Box>

            <Typography
              sx={{
                fontSize: "12px",
                color: "#6B7280",
                mt: 1.5,
              }}
            >
              You can paste HTML content with tags directly, select any text to
              replace with{" "}
              <code
                style={{
                  backgroundColor: "#F3F4F6",
                  padding: "2px 6px",
                  borderRadius: "4px",
                  fontSize: "11px",
                }}
              >
                {"{{ institute_name }}"}
              </code>
              , or use the "Insert Institute Name" button.
            </Typography>
          </Box>

          {loading && (
            <Box sx={{ mt: 3 }}>
              <LinearProgress
                sx={{
                  backgroundColor: "#E5E7EB",
                  height: "6px",
                  borderRadius: "3px",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "#3B82F6",
                    borderRadius: "3px",
                  },
                }}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: "1px solid #E5E7EB",
            px: 3,
            py: 2.5,
            gap: 1.5,
          }}
        >
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              color: "#6B7280",
              px: 3,
              py: 1,
              "&:hover": {
                backgroundColor: "#F3F4F6",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!selectedFile || loading || !selectedMembership}
            sx={{
              backgroundColor: "#3B82F6",
              "&:hover": { backgroundColor: "#2563EB" },
              "&.Mui-disabled": {
                backgroundColor: "#93C5FD",
                color: "#FFFFFF",
              },
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              boxShadow: "none",
              minWidth: "120px",
              px: 3,
              py: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {loading && <AppleStyleSpinner />}
            {loading ? "Creating..." : "Create MOU"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Tooltip on Selection */}
      {showTooltip && (
        <Fade in={showTooltip}>
          <Box
            onMouseDown={insertInstituteNameAtSelection}
            sx={{
              position: "fixed",
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              transform: "translateX(-50%)",
              zIndex: 9999,
              backgroundColor: "#1F2937",
              color: "#FFFFFF",
              padding: "6px 12px",
              borderRadius: "6px",
              fontSize: "11px",
              fontWeight: 500,
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              whiteSpace: "nowrap",
              userSelect: "none",
              "&:hover": {
                backgroundColor: "#374151",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: "-4px",
                left: "50%",
                transform: "translateX(-50%)",
                width: 0,
                height: 0,
                borderLeft: "4px solid transparent",
                borderRight: "4px solid transparent",
                borderTop: "4px solid #1F2937",
              },
            }}
          >
            <CodeIcon sx={{ fontSize: 12 }} />
            {"{{ institute_name }}"}
          </Box>
        </Fade>
      )}
    </>
  );
};

export default CreateMOUModal;
